import { Component, ViewChild, OnInit, Input, ElementRef, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { DomSanitizer} from '@angular/platform-browser';
import {DialogModule} from 'primeng/dialog';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';

// new library imports
import * as docx from 'docx-preview';
import * as XLSX from 'xlsx';

// preserved for compatibility if other parts of the app still rely on it globally
declare var $: any;

@Component({
  selector: 'app-attachment-dialog',
  templateUrl: './attachmentviewer.dialog.component.html',
  styleUrls: [ './attachmentviewer.dialog.component.scss' ],
  providers: [DialogModule]
})
export class AttachmentViewerDialogComponent implements OnInit, OnDestroy  {
  name = 'Document';
  dlgref: any;
  attchBytArr: any;
  display = false;
  enabled = false;
  objectUrl: string | null = null;
  @Input('DocUrl1') sDocUrl1;
  @ViewChild('attchcontainer') elem: ElementRef;
  @Input() isMobile: boolean = false;
  @Output() onPdfViewerOpen = new EventEmitter<string>();

  // property for loading state
  isLoading = false;

  // Properties for PDF viewer
  isPdf = false;
  trustedUrl: any; // Store the trusted URL
  hasError = false; // Add error state
  currentFileName = ''; // Store filename for download

  // Properties for multi-sheet Excel support
  workbook: XLSX.WorkBook | null = null;
  sheetNames: string[] = [];
  activeSheetName: string = '';

  // ViewChild references for the Excel content pane
  @ViewChild('mainContent') mainContent: ElementRef<HTMLElement>;
  @ViewChild('colHeaderPane') colHeaderPane: ElementRef<HTMLElement>;
  @ViewChild('rowHeaderPane') rowHeaderPane: ElementRef<HTMLElement>;
  @ViewChild('contentPane') contentPane: ElementRef<HTMLElement>;

  // Properties for the UI Frame
  columnHeaders: string[] = [];
  rowHeaders: number[] = [];

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly http: HttpClient,
    private readonly dialogservice: DialogModule,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly titleService: Title
  ) { }
  displayDialog() {
    this.enabled = false;
    // now notify angular to check for updates
    this.changeDetector.detectChanges();
    // change detection should remove the component now
    // then we can enable it again to create a new instance
    this.enabled = true; 
    this.display = true;
    return true;
  }
  showDialogWithBlob(file: File): void {
    this.displayDialog();
    this.name = file.name;
    this.currentFileName = file.name;
    this.isLoading = false;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'txt' || ext === 'log') {
      this.renderText(file);
    } else {
      this.loadExcelWorkbook(file);
    }
    this.changeDetector.detectChanges();
  }
  showDialog(sUrl: any, fileextn: string) {
    this.resetView();
    this.resetPdfView();

    this.http.get(sUrl, { responseType: 'blob' }).subscribe({
      next: (blobData) => {
        this.isLoading = false;

        const extension = fileextn.toLowerCase();
        this.currentFileName = `file.${extension}`;
        let mimeType = this.getMimeType(fileextn);

        switch (extension) {
          case 'docx':
            this.renderDocx(blobData);
            break;
          case 'xls':
            this.loadExcelWorkbook(blobData);
            break;
          case 'xlsx':
            this.loadExcelWorkbook(blobData);
            break;
          case 'csv':
            this.loadExcelWorkbook(blobData);
            break;
          case 'pdf':
            this.renderPdf(blobData, mimeType);
            break;
          case 'png':
            this.renderImage(blobData);
            break;
          case 'jpg':
            this.renderImage(blobData);
            break;
          case 'jpeg':
            this.renderImage(blobData);
            break;
          case 'gif':
            this.renderImage(blobData);
            break;
          case 'svg':
            this.renderImage(blobData);
            break;
          case 'log':
            this.renderText(blobData);
            break;
          case 'txt':
            this.renderText(blobData);
            break;
          case 'html':
            this.renderText(blobData);
            break;
          default: // PPTX will now fall into this case
            this.handleRenderError(`File type ".${extension}" is not supported for viewing.`);
            break;
        }
        this.changeDetector.detectChanges();
      },
      error: (error) => {
        this.isLoading = false;
        this.hasError = true;
        console.error('Error fetching the file:', error);
        this.handleRenderError('Error loading file. Please check the console for details.');
        this.changeDetector.detectChanges();
      }
    });
  }

  private async renderDocx(blobData: Blob) {
      const docxOptions = {
        className: 'docx-container', // Class name for the rendered document
        inWrapper: true, // Render in a wrapper element
        ignoreWidth: false, // Respect document width
        ignoreHeight: false, // Respect document height
        ignoreFonts: false, // Respect document fonts
        breakPages: true, // Page breaks
        debug: false,
        experimental: false, // Experimental features
        useMathMLPolyfill: true, // For math formulas
      };

      try {
        // Render the document into the 'attchcontainer' element
        await docx.renderAsync(blobData, this.elem.nativeElement, null, docxOptions);
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.handleRenderError("Could not render DOCX file.", error);
      }
    }

private async loadExcelWorkbook(blobData: Blob) {
  try {
    this.workbook = XLSX.read(await blobData.arrayBuffer(), { type: 'buffer' });
    this.sheetNames = this.workbook.SheetNames;
    this.changeDetector.detectChanges();
    if (this.sheetNames.length > 0) {
      this.selectSheet(this.sheetNames[0]);
    } else {
      this.handleRenderError("The Excel file contains no sheets.");
    }
  } catch (error) {
    this.handleRenderError("Could not read the Excel file.", error);
  }
}

  // Renders the content of a specific sheet by name
  private renderSheet(sheetName: string) {
    if (!this.workbook || !this.mainContent?.nativeElement) return;

    this.activeSheetName = sheetName;
    const worksheet = this.workbook.Sheets[sheetName];

    // Generate the user's data table
    const fullHtml = XLSX.utils.sheet_to_html(worksheet, { id: 'sheetDataTable' });
    const bodyRegEx = /<body[^>]*>([\s\S]*)<\/body>/i;
    const bodyContentMatch = bodyRegEx.exec(fullHtml);
    const tableHtml = bodyContentMatch?.[1] || '';
    this.mainContent.nativeElement.innerHTML = `<div class="xlsx-container">${tableHtml}</div>`;

    // Generate the UI Frame Headers
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const numRows = range.e.r + 1;
    const numCols = range.e.c + 1;

    // Generate row numbers [1, 2, 3, ...]
    this.rowHeaders = Array.from({ length: numRows }, (_, i) => i + 1);

    // Generate column letters ['A', 'B', 'C', ...]
    this.columnHeaders = [];
    for (let i = 0; i < numCols; i++) {
        this.columnHeaders.push(this.getExcelColumnName(i + 1));
    }
  }

  // scroll handler function
  public onContentScroll(): void {
    if (!this.colHeaderPane || !this.rowHeaderPane || !this.contentPane) return;
    this.colHeaderPane.nativeElement.scrollLeft = this.contentPane.nativeElement.scrollLeft;
    this.rowHeaderPane.nativeElement.scrollTop = this.contentPane.nativeElement.scrollTop;
  }
  
  // helper function to generate column letters
  private getExcelColumnName(colNumber: number): string {
    let columnName = '';
    while (colNumber > 0) {
        let remainder = (colNumber - 1) % 26;
        columnName = String.fromCharCode(65 + remainder) + columnName;
        colNumber = Math.floor((colNumber - 1) / 26);
    }
    return columnName;
  }

  // Public function for the HTML template to call when a sheet tab is clicked
  public selectSheet(sheetName: string): void {
    this.renderSheet(sheetName);
  }

  private renderPdf(data: Blob, mimeType: string) {
    const blob = new Blob([data], { type: mimeType });
    this.objectUrl = URL.createObjectURL(blob);
    this.isPdf = true;
    this.isLoading = false;
    this.hasError = false;
    this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    this.onPdfViewerDlgOpen();
  }

  private renderImage(blobData: Blob) {
    this.objectUrl = URL.createObjectURL(blobData);
    const img = document.createElement('img');
    img.src = this.objectUrl;
    img.style.maxWidth = '100%';
    img.style.maxHeight = '100%';
    img.style.objectFit = 'contain';
    this.elem.nativeElement.style.display = 'flex';
    this.elem.nativeElement.style.justifyContent = 'center';
    this.elem.nativeElement.style.alignItems = 'center';
    this.elem.nativeElement.appendChild(img);
  }

  private async renderText(blobData: Blob) {
    try {
      const textContent = await blobData.text();
      const pre = document.createElement('pre');
      pre.textContent = textContent;
      pre.style.whiteSpace = 'pre-wrap';
      pre.style.overflowWrap = 'break-word';
      pre.style.padding = '1rem';
      pre.style.fontFamily = 'monospace';
      this.elem.nativeElement.appendChild(pre);
    } catch (error) {
      this.handleRenderError("Could not render text file.", error);
    }
  }

  private handleRenderError(message: string, error?: any) {
    if (error) console.error(message, error);
    this.elem.nativeElement.innerHTML = `<div style="display: flex; justify-content: center; align-items: center; height: 100%; padding: 2rem; font-size: 1.2rem; text-align: center; color: #dc3545; font-family: sans-serif;">${message}</div>`;
  }

  private resetView() {
    this.isLoading = false;
    this.hasError = false; // Reset error state
    this.sheetNames = [];      // Clear sheet names
    this.workbook = null;      // Clear workbook
    this.activeSheetName = ''; // Clear active sheet
    this.columnHeaders = []; // Clear headers
    this.rowHeaders = [];    // Clear row numbers
    if (this.mainContent?.nativeElement) this.mainContent.nativeElement.innerHTML = '';
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    if (this.elem && this.elem.nativeElement) {
      this.elem.nativeElement.innerHTML = "";
      this.elem.nativeElement.style.display = '';
      this.elem.nativeElement.style.justifyContent = '';
      this.elem.nativeElement.style.alignItems = '';
    }
  }

  private resetPdfView() {
    this.isLoading = false;
    this.isPdf = false;
    this.trustedUrl = null;
    this.hasError = false;
  }
  
  ngOnDestroy() {
    this.resetView();

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  ngOnInit() {
    const originalTitle = this.titleService.getTitle();
    const observer = new MutationObserver(() => {
      if (document.title !== originalTitle) {
        this.titleService.setTitle(originalTitle);
      }
    });
    if (document.querySelector('title')) {
        observer.observe(document.querySelector('title')!, { subtree: true, characterData: true, childList: true });
    }
  }

  /**
   *  whether the dialog is shown
   */
  isVisible() {
    return this.display;
  }

  /**
   * close the dialog
   */
  closeDialog() {
    this.display = false;
    this.resetView();
    this.resetPdfView();
    this.currentFileName = '';
    this.changeDetector.detectChanges();
  }

  /**
   *  customize styles for mobile
   */
  viewStyleObject() {
    if (!this.isMobile) {
      return {width: '800px', minWidth: '800px', height:'600px'};
    } else {
      return {width: '100%', minWidth: '100%', height:'calc(100vh - 66px)'};
    }
  }

  /**
   *  customize styles for mobile
   */
  contentStyleObject() {
    if (!this.isMobile) {
      return {overflow: 'hidden', width:'100%'};
    } else {
      return {overflow: 'auto', width:'100%'};
    }
  }

   getMimeType(fileExtension: string): string {
    const ext = fileExtension.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain',
      'rtf': 'application/rtf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif'
    };

    return mimeTypes[ext] || 'application/octet-stream';
  }

  isKnownUnviewableType(fileExtension: string): boolean {
    const ext = fileExtension.toLowerCase();
    // File types that browsers typically can't display in iframe
    const unviewableTypes = ['doc','ppt', 'pptx'];
    return unviewableTypes.includes(ext);
  }

  onIframeLoad() {
    // Keep showing the iframe
  }

  onIframeError() {
    this.hasError = true;
    this.isPdf = false;
  }

  getFileName(): string {
    return this.currentFileName;
  }
  onPdfViewerDlgOpen() {
     this.onPdfViewerOpen.emit('onPdfViewerOpen');
  }
} 
